// src/modals/ActivateDatingModal.js
import React, { useState, useRef } from 'react';
import { Heart, Camera, AlertCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useModal } from '../hooks';
import userService from '../services/userService';
import { LOOKING_FOR, INTENTIONS } from '../constants';
import { Btn } from '../components/ui';
import PajNtaubBand from '../components/ui/PajNtaubBand';

const ActivateDatingModal = ({ isOpen, onClose, user, onUpdate, toast }) => {
  const { onBackdrop } = useModal(isOpen, onClose);
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [data, setData] = useState({ lookingFor: '', intention: '', profilePictureFile: null });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const needsPhoto = !user?.profilePicture || user.profilePicture.includes('dicebear');

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, photo: t('activateDating.validation.photoSize') }));
      return;
    }

    const img = new Image();
    img.onload = () => {
      if (img.width < 400 || img.height < 400) {
        setErrors((prev) => ({ ...prev, photo: t('activateDating.validation.photoMin') }));
        URL.revokeObjectURL(img.src);
        return;
      }
      setPreview(img.src);
      setData((prev) => ({ ...prev, profilePictureFile: file }));
      setErrors((prev) => ({ ...prev, photo: null }));
    };
    img.src = URL.createObjectURL(file);
  };

  const validate = () => {
    const errs = {};
    if (!data.lookingFor) errs.lookingFor = t('activateDating.validation.lookingFor');
    if (!data.intention) errs.intention = t('activateDating.validation.intention');
    if (needsPhoto && !data.profilePictureFile) errs.photo = t('activateDating.validation.photo');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    const result = await userService.activateDating(user.uid, data);
    setLoading(false);
    if (result.success) {
      onUpdate(result.user);
      toast(t('activateDating.toastSuccess'), 'success');
      onClose();
    } else {
      toast(result.error, 'error');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 animate-fade-in"
      onClick={onBackdrop}
    >
      <div
        data-testid="dating-modal"
        className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-md animate-slide-up max-h-[85dvh] overflow-hidden"
      >
        <PajNtaubBand height={4} />
        <div className="p-4 sm:p-6 overflow-y-auto" style={{ maxHeight: 'calc(85dvh - 4px)' }}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base font-semibold dark:text-gray-100">
              {user?.mode === 'dating' || user?.mode === 'both'
                ? t('activateDating.titleComplete')
                : t('activateDating.title')}
            </h3>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
            >
              <X size={20} />
            </button>
          </div>

          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-gradient-to-br from-rose-500 to-fuchsia-600 rounded-full mx-auto flex items-center justify-center text-white mb-3">
              <Heart size={24} />
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {t('activateDating.subtitle')}
            </p>
          </div>

          <div className="space-y-5">
            {/* lookingFor */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                {t('activateDating.lookingForLabel')}
              </label>
              <div className="flex gap-2">
                {LOOKING_FOR.map((l) => (
                  <button
                    key={l.value}
                    type="button"
                    onClick={() => setData((prev) => ({ ...prev, lookingFor: l.value }))}
                    className={`flex-1 py-3 rounded-2xl font-semibold text-sm transition-all ${
                      data.lookingFor === l.value
                        ? 'bg-rose-500 text-white shadow-md'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {t(l.labelKey)}
                  </button>
                ))}
              </div>
              {errors.lookingFor && (
                <p className="text-red-500 text-sm mt-1">{errors.lookingFor}</p>
              )}
            </div>

            {/* intention */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                {t('activateDating.intentionLabel')}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {INTENTIONS.map((i) => (
                  <button
                    key={i.value}
                    type="button"
                    onClick={() => setData((prev) => ({ ...prev, intention: i.value }))}
                    className={`py-3 px-3 rounded-2xl font-semibold text-sm transition-all ${
                      data.intention === i.value
                        ? 'bg-rose-500 text-white shadow-md'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {t(i.labelKey)}
                  </button>
                ))}
              </div>
              {errors.intention && <p className="text-red-500 text-sm mt-1">{errors.intention}</p>}
            </div>

            {/* Photo (only if needed) */}
            {needsPhoto && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  {t('activateDating.photoLabel')} <span className="text-rose-500">*</span>
                </label>
                <p className="text-xs text-gray-400 mb-3">{t('activateDating.photoHint')}</p>
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center overflow-hidden hover:border-rose-400 transition"
                  >
                    {preview ? (
                      <img
                        src={preview}
                        alt="Preview"
                        className="w-full h-full object-cover rounded-full"
                      />
                    ) : (
                      <Camera size={24} className="text-gray-400" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="px-4 py-2 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-xl text-sm font-semibold hover:bg-rose-100 dark:hover:bg-rose-900/50 transition"
                  >
                    {preview ? t('activateDating.changePhoto') : t('activateDating.addPhoto')}
                  </button>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFile}
                  className="hidden"
                />
                {errors.photo && (
                  <div className="mt-2 flex items-center gap-1 text-red-500 text-sm">
                    <AlertCircle size={14} />
                    <span>{errors.photo}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-6">
            <Btn
              data-testid="dating-modal-cancel"
              v="ghost"
              onClick={onClose}
              className="flex-1 text-sm py-2.5"
            >
              {t('activateDating.cancelBtn')}
            </Btn>
            <Btn
              data-testid="dating-modal-submit"
              onClick={handleSubmit}
              loading={loading}
              className="flex-1 text-sm py-2.5"
            >
              {t('activateDating.submitBtn')}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivateDatingModal;
